/**
 * Keyboard shortcuts.
 *
 * A shortcut is:
 *
 *   {
 *     id: 'impersonate',            // unique
 *     key: 'i',                     // KeyboardEvent.key, lower case for letters
 *     mod: true,                    // optional: Command on Mac, Ctrl on Windows and Linux
 *     group: 'SuperAdmin',          // the overlay's section
 *     description: 'Impersonate a user',
 *     available: true,              // optional: false hides it from the overlay and disables it
 *     handler(event),
 *   }
 *
 * Shortcuts without mod don't fire while the user types in a field, so plain keys like "?" stay usable as text. They do in
 * an empty one-line field, see isTyping.
 */

function detectMac() {
    const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '';
    return /mac|iphone|ipad|ipod/i.test(platform);
}

export const isMac = detectMac();

const KEY_LABELS = {
    ' ': 'Space',
    arrowup: '↑',
    arrowdown: '↓',
    arrowleft: '←',
    arrowright: '→',
    escape: 'Esc',
    enter: isMac ? '↩' : 'Enter',
};

/**
 * @return The keys to show for a shortcut, in the user's system's notation: ['⌘', 'I'] on Mac, ['Ctrl', 'I'] elsewhere
 */
export function shortcutKeys(shortcut) {
    const keys = [];
    if (shortcut.mod) {
        keys.push(isMac ? '⌘' : 'Ctrl');
    }
    const key = shortcut.key.toLowerCase();
    keys.push(KEY_LABELS[key] || (key.length === 1 ? key.toUpperCase() : shortcut.key));
    return keys;
}

/**
 * @return The shortcut as one string, for menus and titles: "⌘I" on Mac, "Ctrl+I" elsewhere
 */
export function shortcutLabel(shortcut) {
    return shortcutKeys(shortcut).join(isMac ? '' : '+');
}

const SINGLE_LINE_TYPES = ['text', 'search', 'email', 'url', 'tel', ''];

/**
 * Whether a plain key goes to a field rather than to the shortcuts.
 *
 * An empty one-line field still lets them through: many pages focus their search box when they open, and nobody starts
 * typing with "?". Fields marked data-no-shortcuts, like the search box over the page, always keep their keys.
 */
function isTyping(event) {
    const target = event.target;
    if (!target || !target.tagName) {
        return false;
    }
    if (target.isContentEditable || ['TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return true;
    }
    if (target.tagName !== 'INPUT') {
        return false;
    }
    const singleLine = SINGLE_LINE_TYPES.includes((target.getAttribute('type') || '').toLowerCase());
    return !singleLine || target.value !== '' || target.hasAttribute('data-no-shortcuts');
}

/**
 * Shift is not compared: it's part of keys like "?". The other modifiers must match exactly.
 */
export function matches(event, shortcut) {
    if (event.key == null || event.key.toLowerCase() !== shortcut.key.toLowerCase()) {
        return false;
    }
    const modPressed = isMac ? event.metaKey : event.ctrlKey;
    const otherModPressed = isMac ? event.ctrlKey : event.metaKey;
    if (modPressed !== Boolean(shortcut.mod) || otherModPressed || event.altKey) {
        return false;
    }
    return shortcut.mod || !isTyping(event);
}

/**
 * @return The available shortcut the event triggers, or undefined
 */
export function findShortcut(event, shortcuts) {
    if (event.isComposing || event.repeat) {
        return undefined;
    }
    return shortcuts.find(shortcut => shortcut.available !== false && matches(event, shortcut));
}
